import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from './LoadingSpinner';
import { validatePassword, sanitizeInput } from '../utils/validation';
import { analytics } from '../utils/analytics';

export default function PasswordResetCompletion() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const { error: showError } = useToast();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);

  useEffect(() => {
    analytics.page('Password Reset Completion');
    
    console.log('[PasswordResetCompletion] Component mounted');
    console.log('[PasswordResetCompletion] Current user:', user?.id || 'none');
    console.log('[PasswordResetCompletion] Current session:', !!session);
    
    // Check if we have a valid session (user should be authenticated after clicking reset link)
    if (user && session) {
      console.log('[PasswordResetCompletion] User is authenticated, can proceed with password reset');
      setIsValidSession(true);
    } else {
      console.log('[PasswordResetCompletion] No valid session found');
      setIsValidSession(false);
    }
  }, [user, session]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0];
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    console.log('[PasswordResetCompletion] Starting password update...');

    try {
      // Update the user's password - AuthContext will handle redirect on USER_UPDATED event
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      console.log('[PasswordResetCompletion] Password update result:', { 
        data: !!data, 
        error: error?.message || 'no error',
        status: error ? 'error' : 'success'
      });

      if (error) {
        console.error('[PasswordResetCompletion] Password update error:', error);
        
        // Handle specific error cases
        if (error.message.includes('same_password') || error.message.includes('422')) {
          showError('Same Password', 'Your new password must be different from your current password.');
        } else if (error.message.includes('session_not_found')) {
          showError('Session Expired', 'Your password reset session has expired. Please request a new password reset.');
        } else if (error.message.includes('invalid_token')) {
          showError('Invalid Token', 'Your password reset link is invalid or has expired. Please request a new one.');
        } else {
          showError('Reset Failed', error.message || 'Failed to update password. Please try again.');
        }
        return;
      }

      // Success case - password was updated successfully
      console.log('[PasswordResetCompletion] Password updated successfully!');
      analytics.track('password_reset_completed');
      
      // Show success message - AuthContext will handle redirect
      showError('Success', 'Password updated successfully! Redirecting...');

    } catch (error: unknown) {
      console.error('[PasswordResetCompletion] Password reset error:', error);
      
      // Handle network or other unexpected errors
      if (error instanceof Error) {
        showError('Reset Failed', error.message || 'An unexpected error occurred. Please try again.');
      } else {
        showError('Reset Failed', 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(sanitizeInput(value));
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(sanitizeInput(value));
    if (errors.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleReturnHome = () => {
    navigate('/', { replace: true });
  };

  // Loading state while checking session
  if (isValidSession === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Checking Reset Link
          </h2>
          <p className="text-gray-600">
            Please wait while we verify your password reset request...
          </p>
        </div>
      </div>
    );
  }

  // Invalid session state
  if (isValidSession === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Invalid Reset Link
          </h2>
          
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired. Please request a new password reset.
          </p>
          
          <button
            onClick={handleReturnHome}
            className="w-full bg-pink-500 text-white px-6 py-3 rounded-xl hover:bg-pink-600 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Password reset form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Set New Password
          </h1>
          <p className="text-gray-600">
            Choose a strong password for your OneWin.Today account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder="Enter your new password"
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                aria-describedby={errors.password ? "password-error" : "password-help"}
                aria-invalid={!!errors.password}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 rounded"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password ? (
              <p id="password-error" className="text-red-500 text-sm mt-1" role="alert">
                {errors.password}
              </p>
            ) : (
              <p id="password-help" className="text-gray-500 text-sm mt-1">
                Must be at least 8 characters with uppercase, lowercase, and number
              </p>
            )}
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                placeholder="Confirm your new password"
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                aria-describedby={errors.confirmPassword ? "confirm-password-error" : undefined}
                aria-invalid={!!errors.confirmPassword}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 rounded"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                disabled={isSubmitting}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p id="confirm-password-error" className="text-red-500 text-sm mt-1" role="alert">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-500 text-white py-3 rounded-xl hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {isSubmitting ? (
              <LoadingSpinner size="sm" color="gray" />
            ) : (
              <>
                <Lock className="h-4 w-4" />
                <span>Update Password</span>
              </>
            )}
          </button>
        </form>

        {/* Security Notice */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-800 text-sm font-medium mb-1">
                Security Notice
              </p>
              <p className="text-blue-700 text-sm">
                After updating your password, you'll be automatically signed in to your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}